import type { CatalogImportRecord, GeneratedBarcode } from '@/api/models';
import { formatInLanguage } from '@/utils/dateLocale';

/**
 * Pure helpers for the Result's labels card (bos-app#232, AC-07): which
 * generated barcodes it lists, how many there are with their units, and the
 * label list's date.
 */

/** How many generated barcodes the card lists; the file has them all. */
export const LABELS_SHOWN = 5;

/** The labels card's figures, or `null` when the import generated none. */
export type LabelsCardData = {
    /** The first {@link LABELS_SHOWN} barcodes, in the order they were minted. */
    shown: GeneratedBarcode[];
    /** Every generated barcode, for the `.xlsx`. */
    rows: GeneratedBarcode[];
    /** How many barcodes the import generated. */
    total: number;
    /** The labels to print: every row's units summed. */
    units: number;
};

/**
 * The card for an import that generated barcodes (`barcodesGenerated` above
 * 0, with the list it recorded); `null` otherwise, and the Result shows no
 * card.
 */
export const labelsCardOf = (
    record: CatalogImportRecord,
): LabelsCardData | null => {
    const rows = record.generatedBarcodes;
    if (record.barcodesGenerated <= 0 || rows.length === 0) {
        return null;
    }
    return {
        shown: rows.slice(0, LABELS_SHOWN),
        rows,
        total: rows.length,
        units: rows.reduce((sum, row) => sum + row.units, 0),
    };
};

/**
 * The import's day for the label list's file name (`label-list-2026-10-14`),
 * in the display zone: the list is the import's, so a download the next day
 * keeps its name.
 */
export const labelListDateOf = (createdAt: string): string =>
    formatInLanguage(createdAt, 'YYYY-MM-DD', undefined);
